const DEFAULT_MIN_WIDTH = 96;
const CELL_HORIZONTAL_PADDING = 24;

const MIN_WIDTH_BY_KIND = {
  actions: 112,
  boolean: 80,
  code: 96,
  date: 104,
  money: 88,
  numeric: 88,
  status: 120,
  text: DEFAULT_MIN_WIDTH,
};

// Espacio que StatusBadge agrega al texto (icono, separación y relleno).
const EXTRA_WIDTH_BY_KIND = {
  status: 40,
};

const getAccessorValue = (row, accessor) => {
  if (typeof accessor === "function") {
    try {
      return accessor(row);
    } catch (_) {
      return "";
    }
  }

  if (typeof accessor !== "string") return "";

  return accessor
    .split(".")
    .reduce((value, key) => value?.[key], row);
};

const getMeasurableValue = (value) => {
  if (value === null || value === undefined) return "";
  if (["string", "number", "boolean"].includes(typeof value))
    return String(value);
  if (value instanceof Date) return String(value);
  return "";
};

const getColumnValue = (row, column) =>
  getAccessorValue(row, column.getSizingValue ?? column.accessor);

const sumWidths = (widths) =>
  widths.reduce((total, width) => total + width, 0);

const collectLeafColumns = (columns, leaves = []) => {
  columns.filter(Boolean).forEach((column) => {
    if (Array.isArray(column.columns)) {
      collectLeafColumns(column.columns, leaves);
      return;
    }

    leaves.push(column);
  });

  return leaves;
};

const createColumnDescriptor = ({
  column,
  data,
  kind,
  getHeaderText,
  measureText,
}) => {
  const explicitMinWidth = Number.isFinite(column.minWidth)
    ? column.minWidth
    : null;
  const inferredMinWidth = MIN_WIDTH_BY_KIND[kind] || DEFAULT_MIN_WIDTH;
  const minWidth = explicitMinWidth ?? inferredMinWidth;

  if (Number.isFinite(column.width)) {
    return {
      fixed: true,
      kind,
      minWidth: column.width,
      preferredWidth: column.width,
      maxWidth: column.width,
    };
  }

  const explicitMaxWidth = Number.isFinite(column.maxWidth)
    ? Math.max(column.maxWidth, minWidth)
    : Number.POSITIVE_INFINITY;
  let measuredWidth = measureText(getHeaderText(column.Header));

  for (const row of Array.isArray(data) ? data : []) {
    measuredWidth = Math.max(
      measuredWidth,
      measureText(getMeasurableValue(getColumnValue(row, column))),
    );

    if (measuredWidth + CELL_HORIZONTAL_PADDING >= explicitMaxWidth) break;
  }

  return {
    fixed: false,
    kind,
    minWidth,
    preferredWidth: Math.max(
      minWidth,
      Math.min(
        explicitMaxWidth,
        measuredWidth + CELL_HORIZONTAL_PADDING + (EXTRA_WIDTH_BY_KIND[kind] || 0),
      ),
    ),
    maxWidth: explicitMaxWidth,
  };
};

const shrinkWidthsLeftToRight = (descriptors, availableWidth) => {
  const widths = descriptors.map(({ preferredWidth }) => preferredWidth);
  let deficit = sumWidths(widths) - availableWidth;

  descriptors.forEach(({ minWidth }, index) => {
    if (deficit <= 0) return;

    const reduction = Math.min(widths[index] - minWidth, deficit);
    widths[index] -= reduction;
    deficit -= reduction;
  });

  return widths;
};

const growWidthsToFill = (descriptors, availableWidth) => {
  const widths = descriptors.map(({ preferredWidth }) => preferredWidth);
  let remainingWidth = availableWidth - sumWidths(widths);
  let flexibleIndexes = descriptors
    .map((_, index) => index)
    .filter((index) => {
      const { fixed, maxWidth, preferredWidth } = descriptors[index];
      return !fixed && maxWidth > preferredWidth;
    });

  while (remainingWidth > 0.5 && flexibleIndexes.length) {
    const equalShare = remainingWidth / flexibleIndexes.length;
    let distributedWidth = 0;

    flexibleIndexes.forEach((index) => {
      const availableForColumn = descriptors[index].maxWidth - widths[index];
      const allocatedWidth = Math.min(equalShare, availableForColumn);
      widths[index] += allocatedWidth;
      distributedWidth += allocatedWidth;
    });

    if (distributedWidth <= 0) break;
    remainingWidth -= distributedWidth;
    flexibleIndexes = flexibleIndexes.filter(
      (index) => descriptors[index].maxWidth - widths[index] > 0.5,
    );
  }

  // La tabla siempre debe ocupar su contenedor. Si todos los límites fueron
  // alcanzados, el remanente se asigna a columnas informativas antes que a
  // checks o acciones compactas.
  if (remainingWidth > 0.5) {
    let fallbackIndexes = descriptors
      .map((_, index) => index)
      .filter((index) =>
        !["actions", "boolean", "fiscalNumber", "taxId"].includes(
          descriptors[index].kind,
        ),
      );
    if (!fallbackIndexes.length)
      fallbackIndexes = descriptors.map((_, index) => index);

    const equalShare = remainingWidth / fallbackIndexes.length;
    fallbackIndexes.forEach((index) => {
      widths[index] += equalShare;
    });
  }

  return widths;
};

const applyLeafWidths = (
  columns,
  widths,
  useExactWidths,
  position = { value: 0 },
) =>
  columns.filter(Boolean).map((column) => {
    if (Array.isArray(column.columns)) {
      return {
        ...column,
        columns: applyLeafWidths(
          column.columns,
          widths,
          useExactWidths,
          position,
        ),
      };
    }

    const width = widths[position.value++];
    if (Number.isFinite(column.width) && !useExactWidths) return column;

    return useExactWidths
      ? { ...column, width }
      : { ...column, minWidth: width };
  });

export const getVisibleTableData = ({
  data = [],
  page = 0,
  pageSize = 20,
  manual = false,
}) => {
  if (!Array.isArray(data) || manual) return data;

  const firstRow = page * pageSize;
  return data.slice(firstRow, firstRow + pageSize);
};

export const sizeDataTableColumns = ({
  columns = [],
  data = [],
  containerWidth = 0,
  getColumnKind,
  getHeaderText,
  measureText,
}) => {
  const leafColumns = collectLeafColumns(columns);
  const descriptors = leafColumns.map((column) =>
    createColumnDescriptor({
      column,
      data,
      kind: getColumnKind(column),
      getHeaderText,
      measureText,
    }),
  );
  const preferredTableWidth = sumWidths(
    descriptors.map(({ preferredWidth }) => preferredWidth),
  );
  const hasContainerWidth =
    Number.isFinite(containerWidth) && containerWidth > 0;
  let widths;

  if (!hasContainerWidth)
    widths = descriptors.map(({ preferredWidth }) => preferredWidth);
  else if (containerWidth < preferredTableWidth)
    widths = shrinkWidthsLeftToRight(descriptors, containerWidth);
  else widths = growWidthsToFill(descriptors, containerWidth);

  return applyLeafWidths(columns, widths, hasContainerWidth);
};
